/**
 * The two behaviours the mockup's koi video lightbox has and Horizon's `<dialog-component>`
 * does not (.mockup/script.js:884-945):
 *
 *   1. Opening plays immediately, with sound. The mockup calls `.play()` the moment the
 *      lightbox is shown and swallows a rejected promise, so a browser that refuses
 *      unmuted playback simply leaves the buyer with the native controls. It never sets
 *      the `autoplay` attribute or `muted`, so the fish is heard, not silently looped.
 *   2. Closing stops the video and rewinds it. The mockup drops the `src` and calls
 *      `load()`; with Shopify's `<source>` children the same `load()` is what returns the
 *      player to its poster, so reopening a card starts from the first frame rather than
 *      resuming mid-swim.
 *
 * A koi card whose first product video is YouTube/Vimeo carries the iframe in a
 * `<template data-koi-card-external-video>` instead. Opening inserts it (its `autoplay=1`
 * stands in for `.play()`) and closing removes it, which both stops the sound and makes
 * reopening start from the beginning.
 *
 * Everything else the mockup does — Escape, backdrop click, the close button, the scroll
 * lock, returning focus to the play pill — `<dialog-component>` and the native modal
 * `<dialog>` already do, so none of it is reimplemented here.
 *
 * Wiring is lazy and delegated from the document: the first press inside a card's
 * `.koi-card__video` attaches that component's listeners. `dialog:open` is dispatched
 * inside a `requestAnimationFrame` after `showModal()`, so a listener added during the
 * click that triggered it is always in place in time — and this survives the theme
 * editor re-rendering the section without needing to re-run the module.
 */

import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

/**
 * Both components that carry the mockup's lightbox: the koi card's own (sections/koi-arrivals.liquid)
 * and `blocks/video.liquid` in its `dialog` player style.
 */
const COMPONENT_SELECTOR = '.koi-card__video, .video-block--dialog';

/** @type {WeakSet<Element>} Components already wired, so a second press does not double-bind. */
const wired = new WeakSet();

/**
 * The player inside a koi card's video component.
 * @param {EventTarget | null} component
 * @returns {HTMLVideoElement | null}
 */
function playerIn(component) {
  if (!(component instanceof Element)) return null;

  const video = component.querySelector('video');
  return video instanceof HTMLVideoElement ? video : null;
}

/** Holds a koi card's YouTube/Vimeo embed until its lightbox opens. */
const EXTERNAL_TEMPLATE_SELECTOR = 'template[data-koi-card-external-video]';

/** The embed inserted from that template; the template's own copy is inert, so never matched. */
const EXTERNAL_EMBED_SELECTOR = '.koi-card__video-embed';

/**
 * Inserts the component's YouTube/Vimeo embed, if it has one.
 * @param {EventTarget | null} component
 * @returns {boolean} Whether the component plays an external video.
 */
function openExternal(component) {
  if (!(component instanceof Element)) return false;

  const template = component.querySelector(EXTERNAL_TEMPLATE_SELECTOR);
  if (!(template instanceof HTMLTemplateElement)) return false;

  if (!component.querySelector(EXTERNAL_EMBED_SELECTOR)) {
    template.after(template.content.cloneNode(true));
  }
  return true;
}

/** @param {Event} event */
function handleOpen(event) {
  if (openExternal(event.currentTarget)) return;

  const video = playerIn(event.currentTarget);
  if (!video) return;

  // Unmuted autoplay can still be refused. The native controls are already on screen,
  // so a refusal costs the buyer one press — never a broken-looking player.
  video.play()?.catch(() => {});
}

/** @param {Event} event */
function handleClose(event) {
  const component = event.currentTarget;
  if (component instanceof Element) {
    component.querySelector(EXTERNAL_EMBED_SELECTOR)?.remove();
  }

  const video = playerIn(component);
  if (!video) return;

  video.pause();
  video.load();
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const component = target.closest(COMPONENT_SELECTOR);
    if (!component || wired.has(component)) return;

    wired.add(component);
    component.addEventListener(DialogOpenEvent.eventName, handleOpen);
    component.addEventListener(DialogCloseEvent.eventName, handleClose);
  },
  { capture: true }
);
